/**
 * React component that renders a stylized "Place" card item for a Board.
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from 'client/shared/js/components/Link'
import MiniCard from '../MiniCard'
import { Title, Body, BtnLink, PlusIcon, CalendarIcon, CoverImage } from '../MiniCardUIComponents'
import { isEmpty } from 'client/app/js/components/RichTextEditor'
import { getPlaceTitle, getPlaceLocation } from '../../utils'
import styles from './MiniPlaceCard.module.scss'

// modes
const suggestion = 'suggestion'
const schedule = 'schedule'

const propTypes = {
  place: PropTypes.object.isRequired,
  photo: PropTypes.object,
  header: PropTypes.node,
  url: PropTypes.string,
  orderNum: PropTypes.number,
  mode: PropTypes.oneOf([suggestion, schedule]),
  onClick: PropTypes.func,
  onBtnClick: PropTypes.func,
  onSeeDetailsClick: PropTypes.func
}

const defaultProps = {
  onClick: () => false,
  onSeeDetailsClick: () => false
}

const MiniPlaceCard = ({ place, photo, header, url, orderNum, mode, onClick, onBtnClick, onSeeDetailsClick }) => {
  const title = useMemo(() => getPlaceTitle(place, orderNum), [place, orderNum])
  const placeLocation = useMemo(() => getPlaceLocation(place), [place])

  const image = useMemo(() => (
    <CoverImage photo={photo || place.photo || place.taPhotos?.[0].images.small.url} />
  ), [place.photo, photo, place.taPhotos])

  const body = useMemo(() => {
    return !isEmpty(place.notes) ? (
      <div title='See notes' className='d-flex font-medium'>
        <FontAwesomeIcon className='font-inherit yellow' icon={['fas', 'note-sticky']} />
      </div>
    ) : null
  }, [place])

  const seeDetails = useMemo(() => {
    if (!onSeeDetailsClick) {
      return null
    }

    const detailsBtn = (
      <BtnLink title='See place details' onClick={onSeeDetailsClick}>
        See details
      </BtnLink>
    )

    return url ? (<Link to={url} anchor silent>{detailsBtn}</Link>) : detailsBtn
  }, [url, onSeeDetailsClick])

  const footer = useMemo(() => {
    let footerRight = null

    switch (mode) {
      case suggestion: {
        footerRight = (<PlusIcon title='Add place to board' onClick={onBtnClick} />)
        break
      }
      case schedule: {
        footerRight = (<CalendarIcon />)
        break
      }
      default:
    }

    return (
      <>
        {seeDetails}
        <span className='ml-auto'>
          {footerRight}
        </span>
      </>
    )
  }, [mode, seeDetails, onBtnClick])

  return (
    <MiniCard
      className={cx(styles['mini-place-card'])}
      image={image}
      header={header}
      footer={footer}
      onClick={onClick}>
      <div>
        <Title>{title}</Title>
        {placeLocation && (
          <Title
            line={mode === 'suggestion' ? 2 : 1}
            size='xxs'
            tagname='h6'>
            {placeLocation}
          </Title>
        )}
      </div>
      <Body>{body}</Body>
    </MiniCard>
  )
}

MiniPlaceCard.propTypes = propTypes
MiniPlaceCard.defaultProps = defaultProps

export default MiniPlaceCard
